"use client";

import { useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  ExternalLink,
  Hash,
  Loader2,
  MessageSquareText,
  Send,
  Settings2,
  Trash2,
} from "lucide-react";
import type { ProgressCard } from "@/lib/types";
import { cn, formatRelative } from "@/lib/utils";

interface SlackBinding {
  id: string;
  slack_team_id: string;
  channel_id: string;
  enabled: boolean;
  post_on_publish: boolean;
  last_message_ts: string | null;
  updated_at: string;
}

interface SlackDelivery {
  id: string;
  progress_card_id: string;
  status: string;
  attempts: number;
  last_error: string | null;
  error_message: string | null;
  needs_sync: boolean;
  sent_at: string | null;
  updated_at: string;
}

interface SlackProjectState {
  connected: boolean;
  can_admin: boolean;
  binding: SlackBinding | null;
  latest_delivery: SlackDelivery | null;
}

export function ProjectSlackUpdates({
  workspaceId,
  projectId,
  latestPublishedCard,
  canWrite,
}: {
  workspaceId: string;
  projectId: string;
  latestPublishedCard: ProgressCard | null;
  canWrite: boolean;
}) {
  const [state, setState] = useState<SlackProjectState | null>(null);
  const [loading, setLoading] = useState(true);
  const [configuring, setConfiguring] = useState(false);
  const [channel, setChannel] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [autoSync, setAutoSync] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<{
    tone: "success" | "error" | "muted";
    text: string;
  } | null>(null);

  const endpoint = `/api/workspaces/${encodeURIComponent(workspaceId)}/projects/${encodeURIComponent(projectId)}/slack-channel`;

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(endpoint, {
        headers: { accept: "application/json" },
        cache: "no-store",
      });
      const data = (await response.json().catch(() => ({}))) as
        | SlackProjectState
        | { error?: string };
      if (!response.ok || !("connected" in data)) {
        throw new Error("Could not load the Slack project channel.");
      }
      setState(data);
      if (data.binding) {
        setChannel(data.binding.channel_id);
        setEnabled(data.binding.enabled);
        setAutoSync(data.binding.post_on_publish);
      }
    } catch (error) {
      setMessage({
        tone: "error",
        text:
          error instanceof Error
            ? error.message
            : "Could not load the Slack project channel.",
      });
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function saveBinding() {
    setSaving(true);
    setMessage(null);
    try {
      const response = await fetch(endpoint, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          channel,
          enabled,
          post_on_publish: autoSync,
        }),
      });
      const data = (await response.json().catch(() => ({}))) as {
        message?: string;
        error?: string;
      };
      if (!response.ok) {
        throw new Error(data.message || "Could not save the Slack channel.");
      }
      setConfiguring(false);
      setMessage({ tone: "success", text: "Slack channel saved." });
      await refresh();
    } catch (error) {
      setMessage({
        tone: "error",
        text:
          error instanceof Error
            ? error.message
            : "Could not save the Slack channel.",
      });
    } finally {
      setSaving(false);
    }
  }

  async function removeBinding() {
    if (!confirm("Stop sending project updates to this Slack channel?")) return;
    setSaving(true);
    setMessage(null);
    try {
      const response = await fetch(endpoint, { method: "DELETE" });
      if (!response.ok) throw new Error("Could not remove the Slack channel.");
      setConfiguring(false);
      setChannel("");
      setMessage({ tone: "success", text: "Slack updates disconnected." });
      await refresh();
    } catch (error) {
      setMessage({
        tone: "error",
        text:
          error instanceof Error
            ? error.message
            : "Could not remove the Slack channel.",
      });
    } finally {
      setSaving(false);
    }
  }

  async function sendLatest() {
    if (!latestPublishedCard || !state?.binding) return;
    setSending(true);
    setMessage(null);
    try {
      const response = await fetch(
        `/api/workspaces/${encodeURIComponent(workspaceId)}/progress-cards/${encodeURIComponent(latestPublishedCard.id)}/slack-sync`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ binding_id: state.binding.id }),
        }
      );
      const data = (await response.json().catch(() => ({}))) as {
        message?: string;
        result?: { status?: string; action?: string };
      };
      if (!response.ok) {
        throw new Error(data.message || "Slack could not deliver this update.");
      }
      const unchanged = data.result?.status === "unchanged";
      setMessage({
        tone: "success",
        text: unchanged
          ? "Slack already has the latest version."
          : data.result?.action === "update"
            ? "Slack update refreshed."
            : "Project update sent to Slack.",
      });
      await refresh();
    } catch (error) {
      setMessage({
        tone: "error",
        text:
          error instanceof Error
            ? error.message
            : "Slack could not deliver this update.",
      });
    } finally {
      setSending(false);
    }
  }

  if (loading && !state) {
    return (
      <div
        role="status"
        className="flex min-h-[96px] items-center justify-center rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] text-[11.5px] text-[var(--fg-muted)]"
      >
        <Loader2 size={12} className="mr-2 animate-spin" />
        Loading Slack updates…
      </div>
    );
  }

  if (!state) {
    return (
      <StatusMessage
        tone="error"
        text={message?.text || "Could not load Slack updates."}
      />
    );
  }

  const binding = state.binding;
  const delivery = state.latest_delivery;
  const showConfig = configuring || (!binding && state.can_admin);

  return (
    <div
      aria-busy={loading || saving || sending}
      className="rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] p-4"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] text-[var(--purple)]">
          <MessageSquareText size={14} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <div className="min-w-0">
              <h2 className="text-[12px] font-medium text-[var(--fg)]">
                Slack project updates
              </h2>
              <p className="mt-0.5 text-[10.5px] leading-relaxed text-[var(--fg-muted)]">
                One living message per project pulse. Changed content updates
                the same Slack message.
              </p>
            </div>
            {state.can_admin && binding && !showConfig && (
              <button
                type="button"
                onClick={() => setConfiguring(true)}
                className="focus-ring ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[var(--fg-tertiary)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg)]"
                aria-label="Configure Slack project updates"
              >
                <Settings2 size={12} />
              </button>
            )}
          </div>

          {!state.connected ? (
            <div className="mt-3">
              <p className="text-[11.5px] text-[var(--fg-tertiary)]">
                Connect Slack before choosing a project channel.
              </p>
              <p className="mt-1 text-[10.5px] leading-4 text-[var(--fg-muted)]">
                The Pauta Slack app must be installed for this workspace before
                project channels can be configured.
              </p>
            </div>
          ) : showConfig ? (
            <form
              className="mt-3 space-y-3"
              onSubmit={(event) => {
                event.preventDefault();
                void saveBinding();
              }}
            >
              <label className="block">
                <span className="mb-1 block text-[10.5px] font-medium text-[var(--fg-secondary)]">
                  Slack channel link
                </span>
                <input
                  value={channel}
                  disabled={saving}
                  onChange={(event) => setChannel(event.target.value)}
                  placeholder="https://sector-crew.slack.com/archives/C…"
                  className="w-full rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-3 py-1.5 text-[12px] text-[var(--fg)] outline-none placeholder:text-[var(--fg-muted)] focus:border-[var(--accent)]"
                />
              </label>
              <label className="flex items-start gap-2 text-[11.5px] text-[var(--fg-secondary)]">
                <input
                  type="checkbox"
                  checked={enabled}
                  disabled={saving}
                  onChange={(event) => setEnabled(event.target.checked)}
                  className="mt-0.5 accent-[var(--accent)]"
                />
                Allow project updates to this channel
              </label>
              <label className="flex items-start gap-2 text-[11.5px] text-[var(--fg-secondary)]">
                <input
                  type="checkbox"
                  checked={autoSync}
                  disabled={saving}
                  onChange={(event) => setAutoSync(event.target.checked)}
                  className="mt-0.5 accent-[var(--accent)]"
                />
                Keep the latest published pulse synced on weekdays
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="submit"
                  disabled={saving || !channel.trim()}
                  className="focus-ring inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-1.5 text-[11.5px] font-medium text-white transition-colors hover:bg-[var(--accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saving && <Loader2 size={11} className="animate-spin" />}
                  Save channel
                </button>
                {binding && (
                  <>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => {
                        setConfiguring(false);
                        setChannel(binding.channel_id);
                        setEnabled(binding.enabled);
                        setAutoSync(binding.post_on_publish);
                      }}
                      className="focus-ring rounded-md px-2 py-1.5 text-[11.5px] font-medium text-[var(--fg-tertiary)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg)]"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => void removeBinding()}
                      disabled={saving}
                      className="focus-ring ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-[11.5px] font-medium text-[var(--red)] transition-colors hover:bg-[color-mix(in_srgb,var(--red)_10%,transparent)]"
                    >
                      <Trash2 size={11} /> Disconnect
                    </button>
                  </>
                )}
              </div>
            </form>
          ) : binding ? (
            <div className="mt-3">
              <div className="flex flex-wrap items-center gap-2">
                <a
                  href={`https://slack.com/app_redirect?channel=${encodeURIComponent(binding.channel_id)}&team=${encodeURIComponent(binding.slack_team_id)}`}
                  target="_blank"
                  rel="noreferrer"
                  title={`Channel ${binding.channel_id}`}
                  className="focus-ring inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-2 py-1 text-[10.5px] font-medium text-[var(--fg-secondary)] transition-colors hover:border-[var(--accent)] hover:bg-[var(--accent-bg)]"
                >
                  <Hash size={10} />
                  Open Slack channel
                  <ExternalLink size={9} />
                </a>
                <span
                  className={cn(
                    "inline-flex items-center gap-1 text-[10.5px]",
                    binding.enabled
                      ? "text-[var(--green)]"
                      : "text-[var(--fg-muted)]"
                  )}
                >
                  <span
                    className={cn(
                      "h-1.5 w-1.5 rounded-full",
                      binding.enabled
                        ? "bg-[var(--green)]"
                        : "bg-[var(--fg-muted)]"
                    )}
                  />
                  {binding.enabled ? "Enabled" : "Paused"}
                </span>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {canWrite && (
                  <button
                    type="button"
                    onClick={() => void sendLatest()}
                    disabled={
                      sending ||
                      !binding.enabled ||
                      !latestPublishedCard
                    }
                    className="focus-ring inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-2.5 py-1.5 text-[11.5px] font-medium text-[var(--fg-secondary)] transition-colors hover:border-[var(--accent)] hover:bg-[var(--accent-bg)] disabled:cursor-not-allowed disabled:opacity-45"
                  >
                    {sending ? (
                      <Loader2 size={11} className="animate-spin" />
                    ) : (
                      <Send size={11} />
                    )}
                    Send latest
                  </button>
                )}
                {delivery ? (
                  <DeliveryStatus delivery={delivery} />
                ) : (
                  <span className="text-[10.5px] text-[var(--fg-muted)]">
                    Not sent yet
                  </span>
                )}
              </div>
              {!latestPublishedCard && (
                <p className="mt-2 text-[10.5px] text-[var(--fg-muted)]">
                  Publish a project progress card to enable sending.
                </p>
              )}
            </div>
          ) : (
            <div className="mt-3">
              <p className="text-[11.5px] text-[var(--fg-tertiary)]">
                No Slack channel is connected to this project.
              </p>
              {!state.can_admin && (
                <p className="mt-1 text-[10.5px] text-[var(--fg-muted)]">
                  A workspace admin can configure one here.
                </p>
              )}
            </div>
          )}

          {message && <StatusMessage tone={message.tone} text={message.text} />}
        </div>
      </div>
    </div>
  );
}

function DeliveryStatus({ delivery }: { delivery: SlackDelivery }) {
  if (delivery.needs_sync) {
    return (
      <span className="inline-flex items-center gap-1 text-[10.5px] text-[var(--yellow)]">
        <Clock3 size={10} />
        Channel changed · send latest
      </span>
    );
  }
  if (delivery.status === "sent") {
    return (
      <span className="inline-flex items-center gap-1 text-[10.5px] text-[var(--fg-muted)]">
        <CheckCircle2 size={10} className="text-[var(--green)]" />
        {delivery.sent_at
          ? `Last synced ${formatRelative(Date.parse(delivery.sent_at))}`
          : "Last synced"}
      </span>
    );
  }
  if (delivery.status === "failed") {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10.5px] text-[var(--red)]"
        title={delivery.error_message || undefined}
      >
        <AlertCircle size={10} />
        Delivery failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10.5px] text-[var(--fg-muted)]">
      <Clock3 size={10} />
      Syncing
    </span>
  );
}

function StatusMessage({
  tone,
  text,
}: {
  tone: "success" | "error" | "muted";
  text: string;
}) {
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={cn(
        "mt-3 rounded-md border px-2.5 py-2 text-[10.5px] leading-relaxed",
        tone === "success"
          ? "border-[color-mix(in_srgb,var(--green)_28%,var(--border))] bg-[color-mix(in_srgb,var(--green)_7%,var(--bg-secondary))] text-[var(--green)]"
          : tone === "error"
            ? "border-[color-mix(in_srgb,var(--red)_28%,var(--border))] bg-[color-mix(in_srgb,var(--red)_7%,var(--bg-secondary))] text-[var(--red)]"
            : "border-[var(--border)] bg-[var(--bg-tertiary)] text-[var(--fg-tertiary)]"
      )}
    >
      {text}
    </div>
  );
}
